import type { QueryClient } from '@tanstack/react-query';
import {
  Outlet,
  createRootRouteWithContext,
  createRoute,
  useNavigate,
} from '@tanstack/react-router';
import { useEffect } from 'react';

import { useAuth } from '@/lib/auth';
import { useOnboardingTourAutoStart } from '@/lib/onboardingTour';
import { useKillerFeaturesWs } from '@/lib/useKillerFeaturesWs';
import { useIsMobile } from '@/lib/useMedia';
import { usePushNavigate } from '@/lib/usePushNavigate';
import { useResetCacheOnUserChange } from '@/lib/useResetCacheOnUserChange';
import { AppShell } from '@/screens/app/AppShell';
import { GroupMembersScreen } from '@/screens/app/GroupMembersScreen';
import { MobileShell } from '@/screens/app/MobileShell';
import { TitleBar } from '@/screens/app/TitleBar';
import { ForgotPasswordScreen } from '@/screens/auth/ForgotPasswordScreen';
import { InviteRedirectScreen } from '@/screens/auth/InviteRedirectScreen';
import { LoginScreen } from '@/screens/auth/LoginScreen';
import { OnboardingScreen } from '@/screens/auth/OnboardingScreen';
import { RegisterScreen } from '@/screens/auth/RegisterScreen';
import { ResetPasswordScreen } from '@/screens/auth/ResetPasswordScreen';
import { PublicEventScreen } from '@/screens/public/PublicEventScreen';
import { PublicExpenseScreen } from '@/screens/public/PublicExpenseScreen';
import { PublicListScreen } from '@/screens/public/PublicListScreen';
import { PublicPollScreen } from '@/screens/public/PublicPollScreen';
import { PublicTodoScreen } from '@/screens/public/PublicTodoScreen';
import { SettingsScreen } from '@/screens/settings/SettingsScreen';

export interface RouterContext {
  queryClient: QueryClient;
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: RootComponent,
});

function RootComponent() {
  const init = useAuth((s) => s.init);
  useEffect(() => {
    void init();
  }, [init]);
  // Synchro WS killer features (events / polls / expenses / todos)
  // active sur toutes les routes auth — y compris pages publiques
  // ouvertes par un membre du groupe.
  useKillerFeaturesWs();
  // Vide le cache TanStack quand l'identité authentifiée change (cf.
  // 10bc1096). Monté ici, au-dessus de tout arbre authentifié : quand `user`
  // repasse à null, l'effet s'exécute après le commit qui a démonté cet arbre,
  // donc aucun observer actif ne peut repartir en fetch sans token.
  useResetCacheOnUserChange();
  // Deep-link push (MAN-143 Phase 2 Task 4) : écoute les messages
  // `push-navigate` du service worker (clic sur une notif alors qu'une
  // fenêtre est déjà ouverte). Monté ici (pas dans AppShell) pour rester
  // actif même hors `/app` (ex : user sur /settings au moment du clic).
  usePushNavigate();
  // Tutoriel de découverte (MAN-217 Phase 1 / MAN-220 Task 4) : déclenche le
  // tuto pour un compte "jamais démarré", quelle que soit la route
  // authentifiée où il atterrit — remplace l'ancien hop impératif unique de
  // `RegisterScreen` (cf. JSDoc de `useOnboardingTourAutoStart`). La reprise
  // ("resume") ne demande aucun code séparé : `useOnboardingTour()` lit
  // directement l'étape déjà persistée.
  useOnboardingTourAutoStart();
  // Window controls Tauri (cf. ADR-026 borderless) — boutons floating
  // intégrés DANS la window via overlay, ancrés à droite. Il n'y a
  // volontairement plus de drag region invisible ici : elle recouvrait les
  // contrôles du haut de fenêtre et mangeait leurs clics, et vit désormais sur
  // les conteneurs de header eux-mêmes (cf. JSDoc de `TitleBar`).
  // En mode navigateur web pur, le composant ne rend rien.
  // Le contenu reste full-height (TitleBar n'occupe pas de place dans le flow).
  return (
    <>
      <TitleBar />
      <Outlet />
    </>
  );
}

/**
 * Le build `@nexus/web` (servi sur app.nexusapp.chat et embarqué dans
 * Tauri) ne sert PAS la landing marketing — celle-ci est un build
 * dédié `@nexus/landing` servi par Caddy sur l'apex `nexusapp.chat`.
 *
 * Donc sur `/`, on redirige toujours vers `/app` (session active) ou
 * `/login` (session absente), peu importe Tauri vs navigateur.
 */
function IndexComponent() {
  const navigate = useNavigate();
  const { user, initializing } = useAuth();

  useEffect(() => {
    if (initializing) return;
    void navigate({ to: user ? '/app' : '/login', replace: true });
  }, [user, initializing, navigate]);

  // Spinner pendant qu'on attend la décision auth — évite le flash de
  // contenu vide. Background via CSS var pour respecter le thème
  // (light/dark, fixé par le ThemeProvider parent).
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--nx-bg)',
        color: 'var(--nx-fg-muted)',
        fontSize: 14,
      }}
    >
      <span style={{ animation: 'spinSlow 1s linear infinite' }}>⟳</span>
    </div>
  );
}

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: IndexComponent,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginScreen,
});
const registerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/register',
  component: RegisterScreen,
});
const forgotRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/forgot-password',
  component: ForgotPasswordScreen,
});
const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reset-password',
  component: ResetPasswordScreen,
});
const onboardingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/onboarding',
  component: OnboardingScreen,
});

const inviteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/invite/$slug',
  component: InviteRedirectScreen,
});

function ResponsiveAppShell() {
  const isMobile = useIsMobile();
  return isMobile ? <MobileShell /> : <AppShell />;
}

const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/app',
  component: ResponsiveAppShell,
});

const settingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings',
  component: SettingsScreen,
});

// MAN-180 Phase 1 Task 4 : écran de gestion des membres d'un groupe (liste +
// promotion/rétrogradation). Page pleine, hors AppShell (même registre que
// `/settings`) — accessible depuis l'entrée "Membres" du `GroupMenu`.
const groupMembersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/groups/$groupId/members',
  component: GroupMembersScreen,
});

const publicEventRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/e/$slug',
  component: PublicEventScreen,
});
const publicPollRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/p/$slug',
  component: PublicPollScreen,
});
const publicExpenseRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/d/$slug',
  component: PublicExpenseScreen,
});
const publicTodoRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/t/$slug',
  component: PublicTodoScreen,
});
const publicListRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/l/$slug',
  component: PublicListScreen,
});

export const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  registerRoute,
  forgotRoute,
  resetPasswordRoute,
  onboardingRoute,
  inviteRoute,
  appRoute,
  settingsRoute,
  groupMembersRoute,
  publicEventRoute,
  publicPollRoute,
  publicExpenseRoute,
  publicTodoRoute,
  publicListRoute,
]);
